// Personnel (/office/personnel, Session ALU). ONE entry point for BOTH people registers, which
// until 24 August 2026 were two similarly-named nav items in two different top-level groups:
// "Personnel compliance" under Compliance and "Engineer compliance" under Admin. That is the same
// collision that made "Scheduling" and "Service schedule" unreadable, and it is fixed the same
// way: one item, two tabs.
//
// THIS IS A SHELL, DELIBERATELY. It renders the two existing pages UNCHANGED -- not a merged page,
// not a shared layout, not one register reading the other's data. The Director's 17 August 2026
// decision (item 5, docs/AIN-DIRECTOR-DECISIONS-17-08-26.md) is that the two REGISTERS stay
// separate ("Both stay, do NOT consolidate"), so nothing below touches either component, their
// data, or the API they call. If a future session is tempted to unify them, that decision is what
// it has to reopen first.
//
// Neither page is given a wrapper heading, on purpose. Both already render their own page heading
// ("Personnel compliance" / "Engineer compliance") and their own container -- Personnel centres
// itself in `mx-auto max-w-5xl`, Engineer runs full width so its competence matrix has room to
// scroll. Adding a "Personnel" heading above the tabs would double-head every view, and imposing one
// container on both would squeeze that matrix. The tab bar names which register you are in; the
// page beneath says it again in its own words, exactly as it does today on its own route.
import { useSearchParams } from "react-router-dom";
import { PersonnelCompliancePage } from "./PersonnelCompliancePage";
import { EngineerCompliancePage } from "./EngineerCompliancePage";

type Tab = "office" | "engineer";

const TABS: Array<{ value: Tab; label: string }> = [
  { value: "office", label: "Office" },
  { value: "engineer", label: "Engineer" },
];

// THE TAB LIVES IN THE URL, which departs from the seven other tabbed office pages (they all hold
// it in useState). The departure is forced, not stylistic: /office/engineer-compliance now
// REDIRECTS here, and the compliance dashboard's expiring-certificate and competence-review rows
// deep-link with ?engineer=<name>, so the destination has to be able to say WHICH tab it means.
// Component state cannot carry that, and landing those rows on the Office tab would silently
// break a working feature (Session AEH, FIX-2 B4). ?tab= cannot collide with ?engineer=, and
// switching tabs preserves every other param rather than resetting the URL.
export function PersonnelPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tab: Tab = searchParams.get("tab") === "engineer" ? "engineer" : "office";

  function selectTab(next: Tab) {
    if (next === tab) return;
    const params = new URLSearchParams(searchParams);
    if (next === "office") params.delete("tab");
    else params.set("tab", next);
    // replace: a tab switch is not a place the back button should have to step through.
    setSearchParams(params, { replace: true });
  }

  return (
    <div>
      {/* Session ACG locked tab treatment: quiet text tabs, purple underline on the active tab
          only (.ele-tabs / .ele-tab in index.css). Chosen over the pill variant because the
          Engineer tab's own 30/60/90-day window filter is already a pill row a few hundred pixels
          down the page, and two pill rows on one screen read as one control repeated. */}
      <div className="ele-tabs mb-5">
        {TABS.map((t) => (
          <button
            key={t.value}
            onClick={() => selectTab(t.value)}
            aria-pressed={tab === t.value}
            className={`ele-tab ${tab === t.value ? "is-active" : ""}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Conditional, not both-mounted-and-hidden. This is what each page does TODAY when you
          navigate to its route and away again: a fresh mount, a fresh load. Keeping both mounted
          would double the API calls on every visit to this page and leave the Personnel page's
          modal state alive behind an invisible tab. */}
      {tab === "engineer" ? <EngineerCompliancePage /> : <PersonnelCompliancePage />}
    </div>
  );
}
